/**
 * T017 — 数据管理（模块十一 4.6）
 *
 * 场景 11.1: 检索与筛选（登记人下拉/任务名关键字/分类下拉/时间区间）；Manager 受 scope，Admin 全局。
 * 场景 11.2: Manager/Admin 行内编辑（分类/任务/处理量/工时/备注），直接生效无重审，写审计。
 * 场景 11.3: 软删（留审计）/ 硬删（Manager/Admin only）。
 * 场景 11.4: 导出 CSV（BOM，CS_Workload_YYYY-MM-DD.csv，当前筛选结果）。
 */

import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../auth'
import { useLang } from '../i18n'
import { TimeRange, TimeRangeValue, defaultTimeRange } from '../components/TimeRange'

// ── Types ──────────────────────────────────────────────────────────────────────

interface WorkLogRow {
  id: string
  member_id: string
  member_display_name: string
  member_region_code: string
  work_date: string
  category_code: string
  category_display_name_en: string
  category_display_name_cn: string
  task_id: string | null
  task_name: string
  volume: number
  hours: string
  note: string
  is_compliant: boolean
  region_code_snapshot: string
  group_name_snapshot: string
  created_at: string
}

interface PageInfo { page: number; page_size: number; total: number }

interface WorkLogListResponse {
  data: WorkLogRow[]
  page: PageInfo
  range: { from: string; to: string }
}

interface CategoryItem {
  id: string
  code: string
  display_name_en: string
  display_name_cn: string
  tasks: TaskItem[]
}

interface TaskItem {
  id: string
  name: string
  category_code: string
  is_active: boolean
  deleted_at: string | null
}

interface MemberItem {
  id: string
  display_name: string
  role: string
}

interface EditState {
  volume: string
  hours: string
  note: string
  category_code: string
  task_id: string
  task_name: string
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function buildListUrl(
  tr: TimeRangeValue,
  memberId: string,
  categoryCode: string,
  keyword: string,
  page: number,
): string {
  const params = new URLSearchParams({ range: tr.range, page: String(page), page_size: '50' })
  if (tr.range === 'custom') {
    if (tr.from) params.set('from', tr.from)
    if (tr.to) params.set('to', tr.to)
  }
  if (memberId) params.set('member_id', memberId)
  if (categoryCode) params.set('category_code', categoryCode)
  if (keyword) params.set('keyword', keyword)
  return `/api/work-logs?${params.toString()}`
}

function buildExportUrl(
  tr: TimeRangeValue,
  memberId: string,
  categoryCode: string,
  keyword: string,
): string {
  const params = new URLSearchParams({ range: tr.range })
  if (tr.range === 'custom') {
    if (tr.from) params.set('from', tr.from)
    if (tr.to) params.set('to', tr.to)
  }
  if (memberId) params.set('member_id', memberId)
  if (categoryCode) params.set('category_code', categoryCode)
  if (keyword) params.set('keyword', keyword)
  return `/api/work-logs/export.csv?${params.toString()}`
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function DataManagementPage() {
  const { t } = useLang()
  const { accessToken, user } = useAuth()
  const role = user?.role ?? ''

  // Filters
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(defaultTimeRange())
  const [memberId, setMemberId] = useState('')
  const [categoryCode, setCategoryCode] = useState('')
  const [keyword, setKeyword] = useState('')
  const [keywordInput, setKeywordInput] = useState('')  // debounced input
  const [page, setPage] = useState(1)

  // Data
  const [data, setData] = useState<WorkLogListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reference data
  const [categories, setCategories] = useState<CategoryItem[]>([])
  const [members, setMembers] = useState<MemberItem[]>([])

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editState, setEditState] = useState<EditState>({
    volume: '', hours: '', note: '', category_code: '', task_id: '', task_name: '',
  })
  const [editError, setEditError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<WorkLogRow | null>(null)
  const [deleteHard, setDeleteHard] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const canEdit = role === 'manager' || role === 'admin'

  // ── Load reference data ────────────────────────────────────────────────────

  useEffect(() => {
    if (!canEdit || !accessToken) return
    const h = { Authorization: `Bearer ${accessToken}` }
    fetch('/api/categories', { headers: h })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => setCategories(d.data ?? []))
      .catch(() => {})

    fetch('/api/members', { headers: h })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => setMembers((d.members ?? []).filter((m: MemberItem) => m.role !== 'admin')))
      .catch(() => {})
  }, [canEdit, accessToken])

  // ── Load work logs ─────────────────────────────────────────────────────────

  const load = useCallback(() => {
    if (role === 'member' || !accessToken) return
    setLoading(true)
    setError(null)
    fetch(buildListUrl(timeRange, memberId, categoryCode, keyword, page), { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}))
          throw new Error(body?.error?.message ?? `HTTP ${r.status}`)
        }
        return r.json() as Promise<WorkLogListResponse>
      })
      .then(d => { setData(d); setLoading(false) })
      .catch((err: Error) => { setError(err.message); setLoading(false) })
  }, [timeRange, memberId, categoryCode, keyword, page, role, accessToken])

  useEffect(() => { load() }, [load])

  // ── Filter handlers ────────────────────────────────────────────────────────

  const resetPage = () => setPage(1)

  const handleKeywordSearch = () => {
    setKeyword(keywordInput)
    resetPage()
  }

  const handleKeywordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleKeywordSearch()
  }

  // ── Export CSV ─────────────────────────────────────────────────────────────

  const handleExport = () => {
    if (!accessToken) return
    const url = buildExportUrl(timeRange, memberId, categoryCode, keyword)
    const a = document.createElement('a')
    a.href = url
    // Auth header cannot be set for anchor download; rely on session cookie or bearer via fetch
    fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(r => {
        if (!r.ok) throw new Error('Export failed')
        return r.blob()
      })
      .then(blob => {
        const objUrl = URL.createObjectURL(blob)
        a.href = objUrl
        a.download = `CS_Workload_${new Date().toISOString().slice(0, 10)}.csv`
        a.click()
        URL.revokeObjectURL(objUrl)
      })
      .catch(() => alert(t('errorGeneric')))
  }

  // ── Inline edit ────────────────────────────────────────────────────────────

  const startEdit = (row: WorkLogRow) => {
    setEditingId(row.id)
    setEditState({
      volume: String(row.volume),
      hours: row.hours,
      note: row.note,
      category_code: row.category_code,
      task_id: row.task_id ?? '',
      task_name: row.task_name,
    })
    setEditError(null)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditError(null)
  }

  const saveEdit = async (rowId: string) => {
    setSaving(true)
    setEditError(null)
    const isOthers = editState.category_code === 'others'
    const body: Record<string, unknown> = {
      volume: Number(editState.volume),
      hours: editState.hours,
      note: editState.note,
      category_code: editState.category_code,
    }
    if (isOthers) {
      body.task_name = editState.task_name
    } else {
      body.task_id = editState.task_id || undefined
    }

    try {
      const r = await fetch(`/api/work-logs/${rowId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${accessToken ?? ''}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}))
        throw new Error(errBody?.error?.message ?? `HTTP ${r.status}`)
      }
      setEditingId(null)
      load()
    } catch (err) {
      setEditError((err as Error).message ?? t('errorGeneric'))
    } finally {
      setSaving(false)
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      const url = `/api/work-logs/${deleteTarget.id}${deleteHard ? '?hard=true' : ''}`
      const r = await fetch(url, { method: 'DELETE', headers: { Authorization: `Bearer ${accessToken ?? ''}` } })
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}))
        throw new Error(errBody?.error?.message ?? `HTTP ${r.status}`)
      }
      setDeleteTarget(null)
      load()
    } catch (err) {
      alert((err as Error).message ?? t('errorGeneric'))
    } finally {
      setDeleting(false)
    }
  }

  // ── Derived ────────────────────────────────────────────────────────────────

  const rows = data?.data ?? []
  const pageInfo = data?.page
  const totalPages = pageInfo ? Math.ceil(pageInfo.total / pageInfo.page_size) : 1

  const tasksForCategory = (catCode: string): TaskItem[] => {
    const cat = categories.find(c => c.code === catCode)
    return (cat?.tasks ?? []).filter(t => t.is_active && !t.deleted_at)
  }

  // ── Forbidden ──────────────────────────────────────────────────────────────

  if (role === 'member') {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ color: 'var(--danger)', fontWeight: 500 }}>{t('dmForbidden')}</p>
      </div>
    )
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const cellStyle: React.CSSProperties = { padding: '8px 10px', fontSize: 13 }
  const inputStyle: React.CSSProperties = {
    padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 13, width: '100%',
    background: 'var(--bg-surface)', color: 'var(--text-primary)',
  }

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--text-primary)' }}>
          {t('navDataMgmt')}
        </h2>
        {canEdit && (
          <button
            onClick={handleExport}
            style={{
              padding: '6px 16px', background: 'var(--accent)', color: 'var(--accent-contrast)',
              border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 500,
            }}
          >
            {t('dmExport')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, alignItems: 'flex-end' }}>
        <TimeRange
          value={timeRange}
          onChange={tr => { setTimeRange(tr); resetPage() }}
        />

        {/* Keyword */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('dmKeyword')}</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <input
              value={keywordInput}
              onChange={e => setKeywordInput(e.target.value)}
              onKeyDown={handleKeywordKeyDown}
              placeholder={t('dmKeywordPlaceholder')}
              style={{ ...inputStyle, width: 180 }}
            />
            <button
              onClick={handleKeywordSearch}
              style={{
                padding: '4px 12px', border: '1px solid var(--border)', borderRadius: 4,
                background: 'var(--bg-surface)', cursor: 'pointer', fontSize: 13,
              }}
            >
              {t('dmSearch')}
            </button>
          </div>
        </div>

        {/* Member filter (Manager/Admin) */}
        {canEdit && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('dmRegistrant')}</label>
            <select
              value={memberId}
              onChange={e => { setMemberId(e.target.value); resetPage() }}
              style={{ ...inputStyle, width: 150 }}
            >
              <option value="">{t('dmAllMembers')}</option>
              {members.map(m => (
                <option key={m.id} value={m.id}>{m.display_name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Category filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('dmCategory')}</label>
          <select
            value={categoryCode}
            onChange={e => { setCategoryCode(e.target.value); resetPage() }}
            style={{ ...inputStyle, width: 160 }}
          >
            <option value="">{t('dmAllCategories')}</option>
            {categories.map(c => (
              <option key={c.code} value={c.code}>
                {c.display_name_en}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary */}
      {data && !loading && (
        <div style={{ marginBottom: 10, fontSize: 13, color: 'var(--text-secondary)' }}>
          {t('dmTotal')}: <strong>{data.page.total}</strong>
          {keyword && (
            <span style={{ marginLeft: 8, color: 'var(--accent)', fontSize: 12 }}>
              {t('dmKeywordActive')}: "{keyword}"
              <button
                onClick={() => { setKeyword(''); setKeywordInput(''); resetPage() }}
                style={{ marginLeft: 4, background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 12 }}
              >
                ✕
              </button>
            </span>
          )}
        </div>
      )}

      {loading && <div style={{ color: 'var(--text-muted)', padding: '16px 0' }}>{t('loading')}</div>}
      {error && <div style={{ color: 'var(--danger)', padding: '8px 0' }}>{error}</div>}

      {/* Table */}
      {!loading && data && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6 }}>
            <thead>
              <tr style={{ background: 'var(--bg-page)', color: 'var(--text-secondary)', fontWeight: 600 }}>
                {[t('dmColId'), t('dmColDate'), t('dmColMember'), t('dmColCategory'), t('dmColTask'), t('dmColVolume'), t('dmColHours'), t('dmColStatus'), t('dmColNote'), canEdit ? t('dmColActions') : null]
                  .filter(Boolean)
                  .map(h => (
                    <th key={h as string} style={{ ...cellStyle, textAlign: 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {h}
                    </th>
                  ))
                }
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 10 : 9} style={{ padding: '32px 12px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    {t('dmNoRecords')}
                  </td>
                </tr>
              ) : rows.map(row => {
                const isEditing = editingId === row.id
                return (
                  <tr
                    key={row.id}
                    style={{ borderBottom: '1px solid var(--border)', background: isEditing ? 'var(--info-bg)' : undefined }}
                  >
                    {/* ID */}
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>
                      {row.id.slice(0, 8)}…
                    </td>

                    {/* Date */}
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{row.work_date}</td>

                    {/* Member + region */}
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                      {row.member_display_name}
                      <span style={{ marginLeft: 4, fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-elevated)', padding: '1px 5px', borderRadius: 3 }}>
                        {row.region_code_snapshot}
                      </span>
                    </td>

                    {/* Category */}
                    <td style={cellStyle}>
                      {isEditing ? (
                        <select
                          value={editState.category_code}
                          onChange={e => setEditState(s => ({ ...s, category_code: e.target.value, task_id: '', task_name: '' }))}
                          style={{ ...inputStyle, width: 130 }}
                        >
                          {categories.map(c => (
                            <option key={c.code} value={c.code}>
                              {c.display_name_en}
                            </option>
                          ))}
                        </select>
                      ) : (
                        row.category_display_name_en
                      )}
                    </td>

                    {/* Task */}
                    <td style={cellStyle}>
                      {isEditing ? (
                        editState.category_code === 'others' ? (
                          <input
                            value={editState.task_name}
                            onChange={e => setEditState(s => ({ ...s, task_name: e.target.value }))}
                            style={{ ...inputStyle, width: 150 }}
                            placeholder="Slack and email follow-up"
                          />
                        ) : (
                          <select
                            value={editState.task_id}
                            onChange={e => setEditState(s => ({ ...s, task_id: e.target.value }))}
                            style={{ ...inputStyle, width: 150 }}
                          >
                            <option value="">{t('wlFormSelectTask')}</option>
                            {tasksForCategory(editState.category_code).map(tk => (
                              <option key={tk.id} value={tk.id}>{tk.name}</option>
                            ))}
                          </select>
                        )
                      ) : (
                        row.task_name
                      )}
                    </td>

                    {/* Volume */}
                    <td style={{ ...cellStyle, textAlign: 'right' }}>
                      {isEditing ? (
                        <input
                          type="number"
                          value={editState.volume}
                          onChange={e => setEditState(s => ({ ...s, volume: e.target.value }))}
                          style={{ ...inputStyle, width: 70 }}
                          min={0}
                        />
                      ) : (
                        row.volume
                      )}
                    </td>

                    {/* Hours */}
                    <td style={{ ...cellStyle, textAlign: 'right' }}>
                      {isEditing ? (
                        <input
                          value={editState.hours}
                          onChange={e => setEditState(s => ({ ...s, hours: e.target.value }))}
                          style={{ ...inputStyle, width: 80 }}
                          placeholder="1.5h"
                        />
                      ) : (
                        `${parseFloat(row.hours).toFixed(2)}h`
                      )}
                    </td>

                    {/* Compliant status */}
                    <td style={cellStyle}>
                      {row.is_compliant ? (
                        <span style={{ color: 'var(--success)', fontSize: 12 }}>✓</span>
                      ) : (
                        <span style={{ color: 'var(--danger)', fontSize: 12, fontWeight: 500 }}>{t('dmNoncompliant')}</span>
                      )}
                    </td>

                    {/* Note */}
                    <td style={{ ...cellStyle, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isEditing ? undefined : 'nowrap' }}>
                      {isEditing ? (
                        <input
                          value={editState.note}
                          onChange={e => setEditState(s => ({ ...s, note: e.target.value }))}
                          style={{ ...inputStyle, width: 160 }}
                          maxLength={200}
                          placeholder={t('dmNotePlaceholder')}
                        />
                      ) : (
                        <span title={row.note}>{row.note || '—'}</span>
                      )}
                    </td>

                    {/* Actions */}
                    {canEdit && (
                      <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                        {isEditing ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {editError && (
                              <span style={{ color: 'var(--danger)', fontSize: 11 }}>{editError}</span>
                            )}
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button
                                onClick={() => saveEdit(row.id)}
                                disabled={saving}
                                style={{ padding: '3px 10px', background: 'var(--success)', color: 'var(--accent-contrast)', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                              >
                                {saving ? t('loading') : t('dmSave')}
                              </button>
                              <button
                                onClick={cancelEdit}
                                style={{ padding: '3px 10px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                              >
                                {t('dmCancel')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              onClick={() => startEdit(row)}
                              style={{ padding: '3px 10px', background: 'var(--bg-surface)', border: '1px solid var(--accent)', color: 'var(--accent)', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                            >
                              {t('dmEdit')}
                            </button>
                            <button
                              onClick={() => { setDeleteTarget(row); setDeleteHard(false) }}
                              style={{ padding: '3px 10px', background: 'var(--bg-surface)', border: '1px solid var(--danger)', color: 'var(--danger)', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                            >
                              {t('dmDelete')}
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {data && totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            style={{ padding: '4px 12px', border: '1px solid var(--border)', borderRadius: 4, background: page <= 1 ? 'var(--bg-page)' : 'var(--bg-surface)', cursor: page <= 1 ? 'not-allowed' : 'pointer', fontSize: 13 }}
          >
            ‹
          </button>
          <span style={{ lineHeight: '30px', fontSize: 13, color: 'var(--text-secondary)' }}>{page} / {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            style={{ padding: '4px 12px', border: '1px solid var(--border)', borderRadius: 4, background: page >= totalPages ? 'var(--bg-page)' : 'var(--bg-surface)', cursor: page >= totalPages ? 'not-allowed' : 'pointer', fontSize: 13 }}
          >
            ›
          </button>
        </div>
      )}

      {/* Delete confirm dialog */}
      {deleteTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'var(--overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: 'var(--bg-surface)', borderRadius: 8, padding: 24, maxWidth: 420, width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>{t('dmDeleteTitle')}</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16 }}>
              {t('dmDeleteBody').replace('{task}', deleteTarget.task_name).replace('{date}', deleteTarget.work_date)}
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={deleteHard}
                onChange={e => setDeleteHard(e.target.checked)}
              />
              {t('dmHardDelete')}
            </label>
            {deleteHard && (
              <p style={{ color: 'var(--danger)', fontSize: 12, margin: '0 0 16px' }}>{t('dmHardDeleteWarning')}</p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteTarget(null)}
                style={{ padding: '6px 16px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--bg-surface)', cursor: 'pointer', fontSize: 13 }}
              >
                {t('dmCancel')}
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                style={{ padding: '6px 16px', background: 'var(--danger)', color: 'var(--accent-contrast)', border: 'none', borderRadius: 4, cursor: deleting ? 'not-allowed' : 'pointer', fontSize: 13 }}
              >
                {deleting ? t('loading') : t('dmDeleteConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
